import { Link, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx"
import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js"

import useProfile from "../../hooks/useProfile.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

// import useProfileDeviceSchemas, { deviceSchemas } from "../../hooks/useProfileDeviceSchemas.js"

export default function ProfileDeviceList() {

    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, profileId } = useParams()
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const devices = useComputed(() => {
        // devices is a subset of profile
        if (profile.value.componentProfile) {
            return profile.value.componentProfile?.config?.deviceConfig?.device ?? []
        }
        return []
    })

    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)

    // const { loading: deviceSchemaLoading } = useProfileDeviceSchemas(config, accessToken)
    
    const showDeleteModal = useSignal(false)
    const confirmDeviceType = useSignal('')
    const confirmTaskDesc = useSignal('')
    const accordionData = [
        { name: 'Devices', data: devices },
        // { name: 'Device Schemas', data: deviceSchemas },
        { name: 'Component Profile', data: profile },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifest }
    ]

    function DeviceTableRows({ devices }) {
        let rows = []
        devices.forEach(device => {
            rows.push(
                <DeviceRow device={device} key={`${device.deviceType}${device.taskDesc}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function DeviceRow({ device }) {
        let deviceType = device.deviceType
        let taskDesc = device.taskDesc
        let encodedDeviceType = encodeURIComponent(deviceType)
        return (
            <tr>
                <td><Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>{device.deviceType}</Link></td>
                <td>{device.taskDesc}</td>
                <td>{device.threadType}</td>
                <td>
                    {canEditSignal.value && <Link to={`./${encodedDeviceType}?desc=${taskDesc}`}><Button variant="outline-primary"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>}
                    {canEditSignal.value && <Button className="mx-2" variant="outline-danger" onClick={() => showModal(deviceType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    function deleteDevice() {
        showDeleteModal.value = false

        // filter out device to be deleted
        const filteredDevices = profile.value.componentProfile.config.deviceConfig.device.filter(device =>
            !(device.deviceType == confirmDeviceType.value && device.taskDesc == confirmTaskDesc.value));

        // set newly filtered device array in componentConfigData    
        profile.value.componentProfile.config.deviceConfig.device = filteredDevices

        //build data object for updateComponentConfig request
        let data = {
            id: profile.value.componentProfile.id,
            name: profile.value.componentProfile.name,
            businessEntityId: profile.value.componentProfile.businessEntityId,
            config: profile.value.componentProfile.config,
        }
                
        // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
        data.config.utgGlobal.ccschema = profileVersion.value

        console.log(data)
        UpdateProfile(beId, profileId, data, config, accessToken)
            .then(result => {
                modified.value = true
                addNotification('The Device was successfully removed.', NotificationTypes.SUCCESS, true)
                profile.value = result
                window.location.reload()
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                isFormSubmitting.value = false
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (deviceType, taskDesc) => {
        // just set the state and the modal will appear
        confirmDeviceType.value = deviceType
        confirmTaskDesc.value = taskDesc
        showDeleteModal.value = true
    }

    function NewDeviceDropdown() {
        if (manifest.value && manifest.value['device-profiles'].length) {
                let deviceOptions = manifest.value['device-profiles'].map(s => {
                    let escapedName = encodeURIComponent(s.name)
                return (
                    <Dropdown.Item href={`/be/${beId}/profiles/${profileId}/devices/${escapedName}`} key={s.name}>{s.name}</Dropdown.Item>
                )
            })

            return (
                <DropdownButton id="dropdown-add-device"
                    variant="outline-secondary"
                    title="New Device"
                    className="mx-3">
                    {deviceOptions}
                </DropdownButton>
            )
        }
    }

    if (!profileIsLoaded.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showDeleteModal.value} handleCancel={() => showDeleteModal.value = false} handleConfirm={() => deleteDevice()} message={`Are you sure you want to delete ${confirmTaskDesc.value}? This action cannot be undone.`} title={`Deleting ${confirmTaskDesc.value}`} />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='Profile Devices' canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="3" debug={debug}></ProfileNav>

        <Table responsive striped hover size="sm">
            <thead>
                <tr>
                    <th>Device Type</th>
                    <th>Device Description</th>
                    <th>Thread Type</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <DeviceTableRows devices={devices.value} />
        </Table>
        {canEditSignal.value && <NewDeviceDropdown />}
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}